import {QuoteDiscountPreview} from "./QuoteDiscounts";
import { useState, useEffect, useRef, useMemo } from "react";
import { useRealtimeMessages } from "@/lib/useRealtimeCases";
import { supabase } from "@/lib/supabaseClient";
import { Case } from "@/lib/useRealtimeCases";
import { EmailPreview } from "./EmailPreview";
import { AttachmentSelector } from "./AttachmentSelector";
import { AttachmentPreviewModal } from "./AttachmentPreviewModal";
import { useEmailTemplates } from "@/lib/useEmailTemplates";
import { interpolateTemplate } from "@/lib/emailTemplateUtils";
import { detectFileType } from "@/lib/fileTypeUtils";
import type { InboundAttachmentMetadata } from "@/lib/messageMerge";
import { Link } from "react-router-dom";
import { Toast } from "../ui/Toast";
import { useNotifications } from "@/lib/useNotifications";
import { FeedbackInvoiceOption } from '../feedback/FeedbackInvoiceOption';

interface ConversationFeedProps {
  caseId: string;
}

export function ConversationFeed({ caseId }: ConversationFeedProps) {
  const { messages, loading } = useRealtimeMessages(caseId);
  const { notifications, markCaseNotificationsRead } = useNotifications();
  const { templates, loading: templatesLoading } = useEmailTemplates();
  const [newNote, setNewNote] = useState("");
  const [includeFeedback, setIncludeFeedback] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [caseData, setCaseData] = useState<Case | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [showAttachmentSelector, setShowAttachmentSelector] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<Array<{id: string; type: string; file_url: string; label: string}>>([]);
  const [customFiles, setCustomFiles] = useState<Array<{id: string; name: string; type: string; size: number; base64: string}>>([]);
  const [attachmentPreview, setAttachmentPreview] = useState<{ attachments: InboundAttachmentMetadata[]; initialIndex: number } | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "warning"; visible: boolean }>({
    message: "",
    type: "success",
    visible: false,
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const unreadMessageCount = useMemo(
    () =>
      notifications.filter(
        (notification) =>
          notification.type === "message" &&
          notification.case_id === caseId &&
          !notification.read_at
      ).length,
    [notifications, caseId]
  );

  const hasUnread = unreadMessageCount > 0;

  const markAsRead = async () => {
    await markCaseNotificationsRead(caseId, "message");
  };

  const showToast = (message: string, type: "success" | "error" | "warning" = "success") => {
    setToast({ message, type, visible: true });
  };

  const hideToast = () => {
    setToast((prev) => ({ ...prev, visible: false }));
  };

  // Charger les données du case pour les templates
  useEffect(() => {
    const fetchCase = async () => {
      const { data } = await supabase
        .from("cases")
        .select("*")
        .eq("id", caseId)
        .single();
      
      if (data) setCaseData(data as Case);
    };
    fetchCase();
  }, [caseId]);

  useEffect(() => {
    // Log minimal en prod pour debug affichage messages
    // eslint-disable-next-line no-console
    console.info("[ConversationFeed] Open case", {
      caseId,
      messagesCount: messages.length,
      loading,
      unreadMessages: unreadMessageCount,
    });
  }, [caseId, messages.length, loading, unreadMessageCount]);

  // Auto-scroll vers le dernier message à l'ouverture
  useEffect(() => {
    if (!loading && messages.length > 0) {
      // Petit délai pour laisser le DOM se stabiliser (ouverture du drawer + rendu)
      const timer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const handleSendEmail = async () => {
    if (!newNote.trim() || submitting) return;

    if (!caseData?.client_email) {
      showToast("Impossible d'envoyer l'email : l'adresse email du client est manquante.", "error");
      return;
    }

    const confirmSend = window.confirm(
      `Envoyer cet email à ${caseData.client_email} ?\n\nLe message sera envoyé et enregistré dans la conversation.`
    );

    if (!confirmSend) return;

    setSubmitting(true);
    try {
      // Récupérer le token d'authentification
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("Session expirée, veuillez vous reconnecter");
      }

      // Appeler la route API pour envoyer l'email via Resend
      const response = await fetch("/api/mail/send", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          caseId,
          templateType: null, // Plus de template système hardcodé
          messageText: newNote.trim(),
          attachmentIds: selectedAttachments.map(a => ({ id: a.id, type: a.type })),
          includeFeedback: includeFeedback && caseData.brand === 'smile' && selectedAttachments.some(a=>a.type==='invoice'),
          // Fichiers personnalisés (non stockés, one-shot)
          customAttachments: customFiles.map(f => ({
            filename: f.name,
            content: f.base64,
            contentType: f.type,
          })),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Erreur lors de l'envoi de l'email");
      }

      console.log("✅ Email envoyé avec succès:", result.emailId);
      
      if (result.warning) {
        showToast(`Email envoyé avec succès ! ${result.warning}`, "warning");
      } else {
        showToast("Email envoyé avec succès !", "success");
      }

      // L'user a répondu → marquer les notifs messages comme lues
      await markCaseNotificationsRead(caseId, "message");

      // Réinitialiser le formulaire
      setNewNote("");
      setIncludeFeedback(false);
      setSelectedAttachments([]);
      setCustomFiles([]);
      setShowAttachmentSelector(false);
    } catch (err: any) {
      console.error("❌ Erreur lors de l'envoi de l'email:", err);
      showToast(`Erreur : ${err.message || "Impossible d'envoyer l'email"}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString("fr-FR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  // Fonction pour utiliser un template user
  const handleTemplateClick = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template || !caseData) return;

    const interpolated = interpolateTemplate(template.body, caseData);
    setNewNote(interpolated);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-500">
        Chargement des messages...
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header avec checkbox "marquer comme lu" */}
      {hasUnread && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <span className="font-medium">💬 Nouveaux messages non lus</span>
          </div>
          <button
            onClick={markAsRead}
            className="text-xs text-red-600 hover:text-red-800 font-medium underline"
          >
            Marquer comme lu
          </button>
        </div>
      )}

      {/* Liste des messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            Aucun message pour le moment
          </div>
        ) : (
          <>
            {messages.map((msg) => {
              const isClient = msg.sender_type === "client";
              const content = typeof msg.content === "string" ? msg.content : "";
              const senderName =
                typeof msg.sender_name === "string" && msg.sender_name.trim()
                  ? msg.sender_name
                  : isClient
                    ? "Client"
                    : "Staff";
              const attachedFiles = msg.metadata?.attachedFiles ?? [];
              const attachments = msg.metadata?.attachments ?? [];

              return (
                <div
                  key={msg.id}
                  className={`flex ${isClient ? "justify-start" : "justify-end"}`}
                >
                  <div
                    className={`max-w-[70%] rounded-lg p-3 ${
                      isClient
                        ? "bg-gray-100 text-gray-900"
                        : "bg-indigo-100 text-indigo-900"
                    }`}
                  >
                    {/* Sender name */}
                    <div className="text-xs font-medium mb-1 opacity-75">
                      {senderName}
                    </div>

                    {/* Content */}
                    <div 
                      className="text-sm break-words whitespace-pre-wrap"
                    >
                      {content}
                    </div>

                    {/* Attachments - Messages sortants (staff) */}
                    {attachedFiles.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200/50 space-y-1">
                        {attachedFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-1.5 text-xs opacity-75">
                            <span>{file.type === "quote" ? "📄" : "🧾"}</span>
                            <span className="font-medium">
                              {file.type === "quote" ? "Devis" : "Facture"}:
                            </span>
                            <span className="truncate">{file.name ?? file.label ?? "Fichier joint"}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Attachments - Messages entrants (clients) */}
                    {attachments.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200/50 space-y-1.5">
                        <div className="text-xs font-medium opacity-75 mb-1">
                          📎 {attachments.length} pièce{attachments.length > 1 ? 's' : ''} jointe{attachments.length > 1 ? 's' : ''} :
                        </div>
                        {attachments.map((attachment, idx) => {
                          const { isImage, isPdf, fileIcon } = detectFileType(attachment.filename, attachment.contentType);
                          const fileSizeKB = attachment.size ? Math.round(attachment.size / 1024) : null;
                          const canPreview = isImage || isPdf;

                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <button
                                onClick={() => canPreview && setAttachmentPreview({ 
                                  attachments,
                                  initialIndex: idx 
                                })}
                                disabled={!canPreview}
                                className={`flex items-center gap-2 text-xs p-2 rounded border border-gray-200 transition-colors flex-1 ${
                                  canPreview 
                                    ? 'bg-white/50 hover:bg-white cursor-pointer' 
                                    : 'bg-gray-50 cursor-default'
                                }`}
                              >
                                <span className="text-base">{fileIcon}</span>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="font-medium truncate">{attachment.filename}</div>
                                  {fileSizeKB && (
                                    <div className="text-gray-500 text-xs">{fileSizeKB} Ko</div>
                                  )}
                                </div>
                                {canPreview && <span className="text-gray-400">👁️</span>}
                              </button>
                              <a
                                href={attachment.url}
                                download={attachment.filename}
                                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Télécharger"
                              >
                                📥
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="text-xs mt-1 opacity-60">
                      {formatDateTime(msg.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Élément invisible pour scroll automatique */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Zone d'ajout de note interne */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Écrire un message
        </label>

        {/* Boutons de templates user */}
        <div className="flex flex-wrap gap-2 mb-3">
          {templatesLoading ? (
            <div className="text-xs text-gray-500">Chargement des templates...</div>
          ) : templates.length === 0 ? (
            <div className="text-xs text-gray-500">
              Aucun template disponible.{" "}
              <Link to="/templates" className="text-indigo-600 hover:underline">
                Créer mon premier template
              </Link>
            </div>
          ) : (
            templates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template.id)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white hover:opacity-80 transition-opacity"
                style={{ backgroundColor: template.color }}
              >
                {template.name}
              </button>
            ))
          )}
          
          {/* Bouton pour créer un template */}
          {!templatesLoading && (
            <Link
              to="/templates"
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 transition-colors"
              title="Créer un nouveau template"
            >
              ➕ Créer un template
            </Link>
          )}
        </div>

        <div className="flex gap-2">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                handleSendEmail();
              }
            }}
            placeholder="Tapez votre message ici ou utilisez un template ci-dessus... (Ctrl+Enter pour envoyer)"
            className="flex-1 border border-gray-300 rounded-md p-2 text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            rows={8}
            disabled={submitting}
          />
        </div>

        {/* Affichage des pièces jointes sélectionnées */}
        {selectedAttachments.some(a=>a.type==='quote') && <QuoteDiscountPreview caseId={caseId} quoteIds={selectedAttachments.filter(a=>a.type==='quote').map(a=>a.id)}/> }
        {import.meta.env.VITE_FEEDBACK_ENABLED === 'true' && caseData?.brand === 'smile' && selectedAttachments.some(a=>a.type==='invoice') && <FeedbackInvoiceOption caseId={caseId} invoiceIds={selectedAttachments.filter(a=>a.type==='invoice').map(a=>a.id)} checked={includeFeedback} onChange={setIncludeFeedback} disabled={submitting}/>}
        {(selectedAttachments.length > 0 || customFiles.length > 0) && (
          <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="text-xs font-medium text-indigo-900 mb-2">
              📎 Pièces jointes ({selectedAttachments.length + customFiles.length}) :
            </div>
            <div className="space-y-1">
              {selectedAttachments.map((att) => (
                <div key={att.id} className="flex items-center justify-between text-xs text-indigo-700">
                  <span>{att.label}</span>
                  <button
                    onClick={() => setSelectedAttachments(prev => prev.filter(a => a.id !== att.id))}
                    className="text-red-600 hover:text-red-800 ml-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
              {customFiles.map((file) => (
                <div key={file.id} className="flex items-center justify-between text-xs text-amber-700">
                  <span>📁 {file.name}</span>
                  <button
                    onClick={() => setCustomFiles(prev => prev.filter(f => f.id !== file.id))}
                    className="text-red-600 hover:text-red-800 ml-2"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-3">
            <div className="text-xs text-gray-600">
              {caseData?.client_email ? (
                <>
                  📧 Email sera envoyé à <strong>{caseData.client_email}</strong>
                </>
              ) : (
                <span className="text-orange-600">⚠️ Email client manquant</span>
              )}
            </div>
            <button
              onClick={() => setShowAttachmentSelector(true)}
              disabled={submitting}
              className="px-3 py-1 text-xs font-medium text-indigo-600 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            >
              📎 Joindre ({selectedAttachments.length + customFiles.length})
            </button>
          </div>
          <button
            onClick={handleSendEmail}
            disabled={!newNote.trim() || submitting || !caseData?.client_email}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Envoi en cours...
              </>
            ) : (
              <>
                <span>📤</span>
                Envoyer
              </>
            )}
          </button>
        </div>
      </div>

      {/* Modal de prévisualisation */}
      {previewHtml && (
        <EmailPreview
          htmlContent={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}

      {/* Modal de sélection des pièces jointes */}
      {showAttachmentSelector && (
        <AttachmentSelector
          caseId={caseId}
          onClose={() => setShowAttachmentSelector(false)}
          onConfirm={(files, custom) => {
            setSelectedAttachments(files);
            setCustomFiles(custom);
            setShowAttachmentSelector(false);
          }}
        />
      )}

      {/* Modal de prévisualisation des pièces jointes */}
      {attachmentPreview && (
        <AttachmentPreviewModal
          isOpen={true}
          onClose={() => setAttachmentPreview(null)}
          attachments={attachmentPreview.attachments}
          initialIndex={attachmentPreview.initialIndex}
        />
      )}

      {/* Toast notifications */}
      <Toast
        message={toast.message}
        type={toast.type}
        isVisible={toast.visible}
        onClose={hideToast}
      />
    </div>
  );
}
